import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useTour} from "../../../providers/tour";
import {reverse} from "../../../routing";
import {gql} from "@apollo/client";
import {Project} from "../../../schema";
import {isEditorIndexTourMock} from "../../../scenes/editor/index.tour";

import ReactLink from "../../ReactLink";
import Skeleton from "../../../ui/skeleton";
import {FolderOutlineIcon} from "../../../ui/icons";

import cn from "classnames";
import classes from "./ProjectTableItem.module.css";

type props = React.ComponentProps<typeof ReactLink> & {
  project: Pick<Project, "name" | "id">
}

export default function ProjectTableItem({className, project, ...props}: props) {
  const {t} = useTranslation();

  const {mock: tourMock} = useTour();
  const mock = useMemo(() => {
    if (tourMock && isEditorIndexTourMock(tourMock)) {
      return tourMock.getBy.project.get(project.id);
    }
  }, [tourMock, project.id])

  return (
    <ReactLink
      className={cn(classes.root, mock?.classes.item, className)}
      title={project.name}
      href={mock?.href ?? reverse("editorProjectEdit", {id: project.id})}
      {...props}
    >
      <span className={cn(classes.name, {[classes.notSpecified]: !!project.name})} translate="no">
        <FolderOutlineIcon className={classes.icon}/>
        {project.name ?? t("common.notSpecified")}
      </span>
    </ReactLink>
  )
}

ProjectTableItem.Skeleton = React.memo(function ({className, ...props}: React.ComponentProps<typeof Skeleton.Block>) {
  return (
    <Skeleton.Block
      className={cn(classes.root, className)}
      height={60}
      {...props}
    />
  )
})

ProjectTableItem.fragments = {
  root: gql`
    fragment ProjectTableItem on Project {
      id
      name
    }
  `
}
